import type { NurseSequenceReorderRequest } from "@/api/nurses";
import { isDispatchedDisplayRow } from "@/components/feature/nurse-membership/splitDispatchedRows";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type {
  InboundInfo,
  InboundItem,
} from "@/hooks/roster-craete/useRosterSchedule";
import type { NurseResponse } from "@/types/nurse-type";
import {
  nurseGroupSortKey,
  sortNurseListByRoleThenSequence,
} from "@/utils/common-util";
import type { QueryKey } from "@tanstack/react-query";

export type RosterCreateRowMoveDirection = "up" | "down";

export type RosterCreateNurseRow = {
  readonly nurse_id?: string | number;
  readonly id?: string | number;
  readonly role?: string | null;
  readonly active?: unknown;
  readonly sequence?: number | null;
  readonly is_inbound?: boolean;
  readonly inbound?: InboundInfo | readonly InboundItem[] | null;
  readonly assignments?: readonly InboundItem[] | null;
};

export type RosterCreatePendingSequenceSave = {
  readonly revision: number;
  readonly payload: NurseSequenceReorderRequest;
  readonly pendingIds: readonly string[];
};

export function rowId(row: RosterCreateNurseRow): string {
  return String(row.nurse_id ?? row.id ?? "");
}

function isInactiveRow(row: RosterCreateNurseRow): boolean {
  return row.active === 0 || row.active === "0" || row.active === false;
}

function isBlockedRow(row: RosterCreateNurseRow): boolean {
  const id = rowId(row);
  return id.trim() === "" || id.startsWith("temp_") || isDispatchedDisplayRow(row);
}

export function findTargetId(
  sourceId: string,
  direction: RosterCreateRowMoveDirection,
  visibleNurseIds: readonly string[]
): string | null {
  const sourceIndex = visibleNurseIds.indexOf(sourceId);
  if (sourceIndex < 0) return null;
  const targetIndex = direction === "up" ? sourceIndex - 1 : sourceIndex + 1;
  return visibleNurseIds[targetIndex] ?? null;
}

function resequenceRows<T extends RosterCreateNurseRow>(rows: readonly T[]): T[] {
  return rows.map((row, index) => ({ ...row, sequence: index + 1 }));
}

export function mergeVisibleOrderIntoSequenceRows<
  T extends RosterCreateNurseRow,
>(sequenceRows: readonly T[], visibleRows: readonly RosterCreateNurseRow[]): T[] {
  const orderedSource = sortNurseListByRoleThenSequence([...sequenceRows]);
  const sourceById = new Map(orderedSource.map((row) => [rowId(row), row]));
  const visibleIds = visibleRows
    .map(rowId)
    .filter((id, index, ids) => {
      const source = sourceById.get(id);
      return source != null && !isBlockedRow(source) && ids.indexOf(id) === index;
    });
  const visibleIdSet = new Set(visibleIds);
  let nextVisibleIndex = 0;

  const mergedRows = orderedSource.map((row) => {
    if (!visibleIdSet.has(rowId(row))) return row;
    const nextId = visibleIds[nextVisibleIndex];
    nextVisibleIndex += 1;
    return nextId == null ? row : (sourceById.get(nextId) ?? row);
  });

  return resequenceRows(mergedRows);
}

export function applySequenceToVisibleRows<T extends RosterCreateNurseRow>(
  visibleRows: readonly T[],
  sequenceRows: readonly RosterCreateNurseRow[]
): T[] {
  const sequenceById = new Map(
    sequenceRows.map((row) => [rowId(row), row.sequence ?? 0])
  );
  return sortNurseListByRoleThenSequence(
    visibleRows.map((row) => {
      const sequence = sequenceById.get(rowId(row));
      return sequence == null ? row : { ...row, sequence };
    })
  );
}

export function swapRows<T extends RosterCreateNurseRow>(
  rows: readonly T[],
  sourceId: string,
  targetId: string
): T[] | null {
  const sourceIndex = rows.findIndex((row) => rowId(row) === sourceId);
  const targetIndex = rows.findIndex((row) => rowId(row) === targetId);
  if (sourceIndex < 0 || targetIndex < 0) return null;

  const source = rows[sourceIndex];
  const target = rows[targetIndex];
  if (!source || !target) return null;
  if (isBlockedRow(source) || isBlockedRow(target)) return null;
  if (isInactiveRow(source) !== isInactiveRow(target)) return null;
  if (nurseGroupSortKey(source) !== nurseGroupSortKey(target)) return null;

  const nextRows = [...rows];
  nextRows[sourceIndex] = target;
  nextRows[targetIndex] = source;
  return resequenceRows(nextRows);
}

export function buildReorderPayload(
  rows: readonly RosterCreateNurseRow[]
): NurseSequenceReorderRequest {
  const active_order: string[] = [];
  const inactive_order: string[] = [];

  rows.forEach((row) => {
    if (isBlockedRow(row)) return;
    const id = rowId(row);
    if (isInactiveRow(row)) {
      inactive_order.push(id);
      return;
    }
    active_order.push(id);
  });

  return { active_order, inactive_order };
}

export function createNurseCacheScopeKeys(groupId?: string | null): QueryKey[] {
  const normalizedGroupId = groupId?.trim() ?? "";
  if (normalizedGroupId === "") {
    return [
      QUERY_KEYS.nurses.listScope(""),
      QUERY_KEYS.nurses.fullSchema(),
      QUERY_KEYS.nurses.fullSchema(""),
    ];
  }

  return [
    QUERY_KEYS.nurses.listScope(normalizedGroupId),
    QUERY_KEYS.nurses.listScope(""),
    QUERY_KEYS.nurses.fullSchema(normalizedGroupId),
    QUERY_KEYS.nurses.fullSchema(),
    QUERY_KEYS.nurses.fullSchema(""),
  ];
}

export function replaceNurseSequenceForCache(
  rows: NurseResponse[] | undefined,
  nextRows: readonly RosterCreateNurseRow[]
): NurseResponse[] | undefined {
  if (!rows) return rows;

  const sequenceById = new Map(
    nextRows.map((row) => [rowId(row), row.sequence ?? 0])
  );

  return sortNurseListByRoleThenSequence(
    rows.map((row) => {
      const sequence = sequenceById.get(row.nurse_id);
      return sequence == null ? row : { ...row, sequence };
    })
  );
}
